import React from "react";

import { PushPinOutlined } from "@mui/icons-material";
import { Avatar, Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { NavLink } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { ContainedIconButton } from "@/components/Button/ContainedIconButton";
import { OutlinedIconButton } from "@/components/Button/OutlinedIconButton";
import { BodyHyperlink } from "@/components/Hyperlinks/BodyHyperlink";
import { Spacer } from "@/components/Spacer/Spacer";
import { Subhead } from "@/components/Typography";
import { ClientListQuery } from "@/gql/graphql";
import BillingStatus from "@/pages/Clients/ClientDetails/Index/components/BillingStatus";

import pinnedClients from "./store/PinnedClients";
import { useSidebarContext } from "../../Sidebar";

interface Props {
  client: ClientListQuery["clients"]["nodes"][0];
}

const useStyles = makeStyles()((theme) => ({
  link: {
    textDecoration: "none",
    color: theme.palette.common.black,
  },
  avatar: {
    width: "40px",
    height: "40px",
  },
}));

const ClientItem: React.FC<Props> = observer(({ client }) => {
  const { classes } = useStyles();
  const { dispatch } = useSidebarContext();

  // Functions
  const handleTogglePin = (
    clientId: string,
    evt: React.MouseEvent<SVGSVGElement, MouseEvent>
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    pinnedClients.toggle(clientId);
  };

  return (
    <NavLink
      to={`/clients/${client?.id}/profile`}
      key={client?.id}
      className={classes.link}
      onClick={() =>
        dispatch({
          type: "close-component",
        })
      }
    >
      <Box display="flex" py="12px" alignItems="center">
        {pinnedClients.has(client.id) ? (
          <ContainedIconButton
            icon={
              <PushPinOutlined
                onClick={(evt) => handleTogglePin(client.id, evt)}
                fontSize="small"
              />
            }
            size="smaller"
          />
        ) : (
          <OutlinedIconButton
            icon={
              <PushPinOutlined
                onClick={(evt) => handleTogglePin(client.id, evt)}
                fontSize="small"
              />
            }
            size="smaller"
          />
        )}
        <Spacer w={16} />
        <Avatar
          src={client?.profile?.avatarUrl ?? undefined}
          className={classes.avatar}
          alt={"User"}
          imgProps={{
            loading: "lazy",
          }}
        />
        <Spacer w={16} />
        <Box>
          <BodyHyperlink text={client?.profile?.fullName} />
          <Subhead text={client?.programForDate?.name ?? "No plan"} />
        </Box>
        <Box display="flex" alignSelf="stretch" marginLeft="auto">
          <BillingStatus status={client?.status} />
        </Box>
      </Box>
    </NavLink>
  );
});

export default ClientItem;
